import React, { useEffect, useState } from "react";
import { Form, InputNumber, Button, message, Input, Popconfirm } from "antd";
import inputStyles from "../../../styles/Custom/Input.module.css";
import btnStyles from "../../../styles/Custom/Button.module.css";
import PaymentModeSelector from "../../PatientsIP/ReceptionistIp/BillingIp/PaymentModeSelector";
import { recordPharmacyPaymentApi } from "../../../services/pharmacyBilling.services";
import {
  createCashAmountRule,
  syncCashAmountValidation,
  validateCashPaymentAmount,
} from "../../../helpers/paymentValidation";
import styles from "../../PatientsIP/ReceptionistIp/BillingIp/BillingIp.module.css";
import payStyles from "./billPayment.module.css";
import CgIcon from "../common/CgIcon";
import { formatMoney } from "../common/money";

const PaymentModeField = ({ value, onChange, form }) => (
  <PaymentModeSelector
    value={value}
    onChange={(mode) => {
      onChange?.(mode);
      syncCashAmountValidation(form, mode, "amount");
    }}
  />
);

const PaymentForm = ({ bill, refreshData }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const paymentMode = Form.useWatch("payment_mode", form);
  const enteredAmount = Form.useWatch("amount", form);
  const balanceDue = Number(bill?.grand_total || 0) - Number(bill?.paid_amount || 0);
  const balanceAfter = balanceDue - (Number(enteredAmount) || 0);

  useEffect(() => {
    syncCashAmountValidation(form, paymentMode, "amount");
  }, [paymentMode, form]);

  /* A different bill, or one that has just been paid against, starts empty -
     the Outstanding pill is one press away when the whole balance is wanted. */
  useEffect(() => {
    form.setFieldsValue({ amount: undefined });
  }, [bill?.id, bill?.paid_amount, form]);

  const onFinish = (values) => {
    const cashError = validateCashPaymentAmount(values?.amount, values?.payment_mode);
    if (cashError) {
      message.error(cashError);
      return;
    }
    setLoading(true);
    /*
     * The card used to carry two tabs, Record payment and Final settlement, and
     * the tab chose the type. The mockup has one card, so the amount chooses
     * instead: paying off the whole outstanding balance is what a final
     * settlement is, and anything short of it is a payment against the bill.
     */
    const settlesBill = Number(values.amount) >= balanceDue - 0.009;

    recordPharmacyPaymentApi(bill.id, {
      payment_type: settlesBill ? "settlement" : "advance",
      payment_mode: values.payment_mode,
      amount: values.amount,
      transaction_reference: values.transaction_reference,
      remarks: values.remarks,
    })
      .then(() => {
        message.success("Payment recorded");
        form.resetFields();
        refreshData();
      })
      .catch((err) => {
        const data = err?.response?.data;
        const msg =
          data?.message ||
          data?.amount?.[0] ||
          data?.non_field_errors?.[0] ||
          "Failed to record payment";
        message.error(msg);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Form layout="vertical" onFinish={onFinish} form={form}>
      <label className={styles.formLabel} htmlFor={`pharmacy-payment-amount-${bill?.id}`}>
        Amount
      </label>
      <Form.Item
        name="amount"
        className={styles.amountInput}
        rules={[
          { required: true, message: "Please enter amount" },
          createCashAmountRule(form, "payment_mode"),
        ]}
      >
        <InputNumber
          id={`pharmacy-payment-amount-${bill?.id}`}
          addonBefore="₹"
          controls={false}
          min={0.01}
          placeholder="0.00"
          className={`${inputStyles.mdNumberInput} ${styles.settlementAmountInput}`}
        />
      </Form.Item>

      {/* The figure almost every payment is for, one press away. */}
      {balanceDue > 0 && (
        <div className={payStyles.quick}>
          <button
            type="button"
            className={payStyles.q}
            onClick={() =>
              form.setFieldsValue({ amount: Number(balanceDue.toFixed(2)) })
            }
          >
            Outstanding · {formatMoney(balanceDue)}
          </button>
          {enteredAmount ? (
            <button
              type="button"
              className={payStyles.q}
              onClick={() => form.setFieldsValue({ amount: undefined })}
            >
              Clear
            </button>
          ) : null}
        </div>
      )}

      <label className={styles.formLabel} style={{ marginTop: 16 }}>
        Payment mode
      </label>
      <Form.Item
        name="payment_mode"
        rules={[{ required: true, message: "Please select a payment mode" }]}
      >
        <PaymentModeField form={form} />
      </Form.Item>

      {paymentMode && paymentMode !== "cash" && (
        <>
          <label className={styles.formLabel} style={{ marginTop: 16 }}>
            Transaction number
          </label>
          <Form.Item name="transaction_reference">
            <Input className={inputStyles.mdInput} placeholder="Enter transaction ID" />
          </Form.Item>
        </>
      )}

      <label className={styles.formLabel} style={{ marginTop: 16 }}>
        Remarks
      </label>
      <Form.Item name="remarks" className={styles.remarksInput}>
        <Input.TextArea placeholder="Optional note for receipt" rows={3} />
      </Form.Item>

      <div
        className={`${payStyles.due} ${balanceAfter <= 0.009 ? payStyles.dueClear : ""}`}
      >
        <span>
          {balanceAfter > 0.009
            ? "Balance after this payment"
            : "Nothing left to collect"}
        </span>
        <b>{formatMoney(Math.max(balanceAfter, 0))}</b>
      </div>

      <div className={styles.settlementActions}>
        <Popconfirm
          title="Payment confirmation"
          description="Have you verified the payment details?"
          okText="Yes"
          cancelText="No"
          onConfirm={() => form.submit()}
        >
          <Button
            loading={loading}
            type="primary"
            htmlType="button"
            className={`${btnStyles.lgBtn} ${styles.submitBtn}`}
            icon={<CgIcon name="bolt" size={16} stroke={2} />}
          >
            Collect payment
          </Button>
        </Popconfirm>
      </div>
    </Form>
  );
};

const PharmacyBillRecordPayment = ({ bill, refreshData }) => {
  const isCancelled = bill?.status === "cancelled";

  if (isCancelled) {
    return (
      <section className={styles.paymentCard}>
        <div className={styles.paymentSummaryList}>
          <div className={styles.paymentSummaryRow}>
            <span>Bill total</span>
            <strong>{formatMoney(bill?.grand_total)}</strong>
          </div>
          <div className={styles.paymentSummaryRow}>
            <span>Paid</span>
            <strong>{formatMoney(bill?.paid_amount)}</strong>
          </div>
        </div>
        <p className={styles.emptyHistory} style={{ margin: 0 }}>
          This bill is cancelled. Payments are closed.
        </p>
      </section>
    );
  }

  /*
   * One card, titled - the pair of tabs is gone.
   *
   * "Final settlement" was the same form with the amount locked to the balance;
   * pressing Outstanding in the quick row does that in one press, and what it
   * produces is recorded as a settlement because it settles the bill. Two tabs
   * for one act was a choice the pharmacist had to make before knowing what
   * they were about to type.
   */
  return (
    <section className={styles.paymentCard}>
      <div className={payStyles.head}>
        <p>Record payment</p>
      </div>
      <div className={styles.paymentFormBody}>
        <PaymentForm
          key={`payment-${bill.id}-${bill.paid_amount}`}
          bill={bill}
          refreshData={refreshData}
        />
      </div>
    </section>
  );
};

export default PharmacyBillRecordPayment;
